import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Users, Store as StoreIcon } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Label } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { RatingFormSettings } from "../../components/RatingFormSettings";

const MAX_TOTAL = 10;

function normaliseQs(initial) {
  return (initial || [])
    .map((q) => (typeof q === "string" ? { text: q, scope: "waiter" } : { scope: "waiter", ...q }))
    .filter((q) => q && typeof q.text === "string");
}

function ScopeColumn({ scope, rows, canAdd, onAdd, onChange, onRemove, testId }) {
  const isStore = scope === "store";
  const Icon = isStore ? StoreIcon : Users;
  const tone = isStore
    ? { border: "border-amber-200", header: "bg-amber-50 text-amber-900 border-amber-200", btn: "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100", title: "Rate the store", sub: "Feeds Store KPI only" }
    : { border: "border-indigo-200", header: "bg-indigo-50 text-indigo-900 border-indigo-200", btn: "border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100", title: "Rate your server", sub: "Counts to each waiter's SSI" };

  return (
    <div className={`flex-1 rounded-xl border bg-white ${tone.border}`} data-testid={`${testId}-col-${scope}`}>
      <div className={`flex items-center gap-2 rounded-t-xl border-b px-4 py-3 ${tone.header}`}>
        <Icon className="h-4 w-4" />
        <div>
          <div className="text-xs font-bold uppercase tracking-wider">{tone.title}</div>
          <div className="text-xs opacity-70">{tone.sub}</div>
        </div>
      </div>
      <div className="space-y-2 p-3">
        {rows.length === 0 && (
          <p className="px-1 py-4 text-xs text-slate-400">No questions yet. Tap "Add question" to add one.</p>
        )}
        {rows.map((row) => (
          <div key={row.id} className="flex items-center gap-2" data-testid={`${testId}-row-${scope}-${row.idx}`}>
            <Input value={row.text} maxLength={120}
              onChange={(e) => onChange(row.id, e.target.value)}
              placeholder={`Yes/No question ${row.idx + 1}`}
              data-testid={`${testId}-q-${scope}-${row.idx}`}
              className="flex-1" />
            <button type="button" onClick={() => onRemove(row.id)}
              aria-label="Remove question"
              data-testid={`${testId}-remove-${scope}-${row.idx}`}
              className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <button type="button" onClick={onAdd} disabled={!canAdd}
          data-testid={`${testId}-add-${scope}`}
          className={`mt-1 inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${tone.btn} ${!canAdd ? "cursor-not-allowed opacity-40" : ""}`}>
          <Plus className="h-3.5 w-3.5" /> Add question
        </button>
      </div>
    </div>
  );
}

function QuestionEditor({ title, hint, initial, onSave, testId }) {
  // local rows carry a stable `id` so inputs don't lose focus on each keystroke
  const [rows, setRows] = useState(() =>
    normaliseQs(initial).map((q, i) => ({ ...q, id: `r-${i}-${Math.random().toString(36).slice(2, 7)}` }))
  );

  const waiter = useMemo(() => rows.filter((r) => (r.scope || "waiter") === "waiter").map((r, idx) => ({ ...r, idx })), [rows]);
  const store = useMemo(() => rows.filter((r) => r.scope === "store").map((r, idx) => ({ ...r, idx })), [rows]);
  const total = rows.length;
  const canAdd = total < MAX_TOTAL;

  const addRow = (scope) => () => {
    if (!canAdd) { toast.error(`Up to ${MAX_TOTAL} questions total`); return; }
    setRows((cur) => [...cur, { id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, text: "", scope }]);
  };
  const changeRow = (id, text) => setRows((cur) => cur.map((r) => (r.id === id ? { ...r, text } : r)));
  const removeRow = (id) => setRows((cur) => cur.filter((r) => r.id !== id));

  const handleSave = () => {
    const clean = rows
      .map((r) => ({ text: (r.text || "").trim(), scope: r.scope || "waiter" }))
      .filter((q) => q.text);
    onSave(clean);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <Label>{title}</Label>
          {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
        </div>
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600" data-testid={`${testId}-counter`}>
          {total}/{MAX_TOTAL}
        </span>
      </div>

      <div className="mt-4 flex flex-col gap-3 lg:flex-row">
        <ScopeColumn scope="waiter" rows={waiter} canAdd={canAdd}
          onAdd={addRow("waiter")} onChange={changeRow} onRemove={removeRow} testId={testId} />
        <ScopeColumn scope="store" rows={store} canAdd={canAdd}
          onAdd={addRow("store")} onChange={changeRow} onRemove={removeRow} testId={testId} />
      </div>

      <div className="mt-4 flex justify-end">
        <Button className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid={`${testId}-save`} onClick={handleSave}>
          Save questions
        </Button>
      </div>
    </div>
  );
}

export default function RatingQuestions() {
  const [data, setData] = useState(null);
  const load = () => api.get("/rating-questions").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const [applyAll, setApplyAll] = useState(true);
  useEffect(() => { if (data) setApplyAll(data.apply_group_to_all); }, [data]);

  if (!data) return <div className="text-slate-400">Loading…</div>;

  const saveGroup = async (questions) => {
    try { await api.put("/rating-questions", { group_questions: questions, apply_group_to_all: applyAll }); toast.success("Group questions saved"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveLoc = (id) => async (questions) => {
    try { await api.put(`/locations/${id}/rating-questions`, { questions }); toast.success("Location questions saved"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="rating-questions-page">
      <div>
        <Label>Ratings</Label>
        <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Rating Form &amp; Recovery</h1>
        <p className="mt-1 text-sm text-slate-500">
          Customers see: star rating → <span className="font-semibold text-indigo-700">Rate your server</span> → <span className="font-semibold text-amber-700">Rate the store</span> → comment.
          Place each question in the column it belongs to — no per-row toggles needed.
        </p>
      </div>

      {data.can_edit_group && <RatingFormSettings />}

      <div>
        <Label>2 · Yes/No questions (shown after the touchpoints)</Label>
        <p className="mt-1 text-sm text-slate-500">Up to {MAX_TOTAL} questions total across both columns. Leave empty to skip this step.</p>
      </div>

      {data.can_edit_group && (
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-xl border border-indigo-200 bg-indigo-50 p-4">
            <div>
              <p className="text-sm font-semibold text-slate-800">Apply group questions to all locations</p>
              <p className="text-xs text-slate-500">On: every location uses the group set. Off: each location uses its own.</p>
            </div>
            <Switch checked={applyAll} onCheckedChange={setApplyAll} data-testid="apply-all-toggle" />
          </div>
          <QuestionEditor title="Group questions (all locations)" initial={data.group_questions} onSave={saveGroup} testId="group-questions" />
        </div>
      )}

      <div className="space-y-4">
        <Label>Per-location questions</Label>
        {data.locations.map((l) => (
          <QuestionEditor key={l.id} title={l.name}
            hint={data.can_edit_group && applyAll ? "Group questions are currently applied to all locations — these are used only when that toggle is off." : "Used for this location's feedback flow."}
            initial={l.questions} onSave={saveLoc(l.id)} testId={`loc-questions-${l.id}`} />
        ))}
      </div>
    </div>
  );
}
